import React, { Component } from 'react';
import {
  graphql,
  createPaginationContainer,
  type RelayPaginationProp,
} from 'react-relay';
import { bindActionCreators } from 'redux';
import { formValueSelector, initialize, reset } from 'redux-form';
import { TableRow, TableRowColumn } from 'material-ui/Table';
import CreateIcon from 'material-ui/svg-icons/content/create';
import IconButton from 'material-ui/IconButton';
import RaisedButton from 'material-ui/RaisedButton';
import Moment from 'react-moment';

import { Grid, Loader } from '../../components';
import Well from '../../components/Well';
import JobsFilter from './JobsFilter';
import enhancer from '../enhancer';

const JobStatus = ({ value }) => <span>{value.replace('JobStatus.', '')}</span>;

const styles = {
  toolbarButton: {
    padding: 5,
  },
};

const PAGE_SIZE = 20;

type Props = {} & RelayPaginationProp;

type State = {
  error: ?Error,
  loading: boolean,
};

class Jobs extends Component<Props, State> {
  state = {
    loading: false,
    error: null,
  };

  handleLoadMore = () => {
    this.setState({ loading: true });
    if (!this.props.relay.hasMore() || this.props.relay.isLoading()) {
      return;
    }

    this.props.relay.loadMore(PAGE_SIZE, error =>
      this.setState({ loading: false, error })
    );
  };

  handleReload = () => {
    this.setState({ loading: true });
    this.props.relay.refetchConnection(PAGE_SIZE, error =>
      this.setState({ loading: false, error })
    );
  };

  handleEdit = (event, jobId) => {
    this.props.history.push(`/admin/job/${jobId}`);
    event.preventDefault();
  };

  handleResetFilter = () => {
    this.props.actions.reset('jobsForm');
  };

  handleFilter = () => {
    const { hasOpenJobOffers, student, ...filters } = this.props.filterValues;

    this.setState({ loading: true });

    this.props.relay.refetchConnection(
      PAGE_SIZE,
      error => this.setState({ loading: false, error }),
      {
        ...filters,
        hasOpenJobOffers: hasOpenJobOffers ? true : null,
        studentId: student ? student.value : null,
      }
    );

    this.props.actions.initialize('jobsForm', this.props.filterValues);
  };

  renderRow = ({ node }) => {
    const completedSessions = node.sessions.filter(
      session => session.status === 'JobSessionStatus.Completed'
    ).length;
    const pendingSessions = node.sessions.filter(
      session => session.status === 'JobSessionStatus.Started'
    ).length;

    return (
      <TableRow key={node.id}>
        <TableRowColumn>
          <Moment date={node.createdAt} format="D MMM HH:mm" />
        </TableRowColumn>
        <TableRowColumn>
          {node.acceptedJobOffer.jobRequest.student.fullname}
        </TableRowColumn>
        <TableRowColumn>{node.acceptedJobOffer.tutor.fullname}</TableRowColumn>
        <TableRowColumn>
          <JobStatus value={node.status} />
        </TableRowColumn>
        <TableRowColumn>{node.sessionCount}</TableRowColumn>
        <TableRowColumn>{node.averageTutorRating} / 5</TableRowColumn>
        <TableRowColumn>{node.averageStudentRating} / 5</TableRowColumn>
        <TableRowColumn>{pendingSessions}</TableRowColumn>
        <TableRowColumn>{completedSessions}</TableRowColumn>
        <TableRowColumn>
          {node.acceptedJobOffer.jobRequest.subject.description}
        </TableRowColumn>
        <TableRowColumn>
          {node.acceptedJobOffer.jobRequest.grade.name}
        </TableRowColumn>

        <TableRowColumn>
          <IconButton
            href={`/admin/job/${node.id}`}
            onClick={event => {
              this.handleEdit(event, node.id);
            }}
          >
            <CreateIcon />
          </IconButton>
        </TableRowColumn>
      </TableRow>
    );
  };

  render() {
    const { admin } = this.props;
    const { allJobs } = admin;
    const { edges, pageInfo } = allJobs;

    const { error, loading } = this.state;

    const headers = [
      'Created At',
      'Student',
      'Tutor',
      'Status',
      'Sessions',
      'Tutor Rating',
      'Student Rating',
      'Pending Sessions',
      'Completed Sessions',
      'Subject',
      'Grade',
      'Actions',
    ];

    return (
      <Well>
        <form>
          <JobsFilter
            handleFilter={this.handleFilter}
            handleResetFilter={this.handleResetFilter}
            filterValues={this.props.filterValues}
          />

          {loading ? <Loader /> : null}
          {error && <div>{error.messge}</div>}

          <Grid headers={headers} renderRow={this.renderRow} rows={edges} />
        </form>

        <span style={styles.toolbarButton}>
          <RaisedButton
            disabled={!pageInfo.hasNextPage || loading}
            primary
            label="Load More"
            onClick={this.handleLoadMore}
          />
        </span>
        <span style={styles.toolbarButton}>
          <RaisedButton
            disabled={loading}
            secondary
            label="Reload"
            onClick={this.handleReload}
          />
        </span>
      </Well>
    );
  }
}

const selector = formValueSelector('jobsForm');

const query = graphql`
  query JobsAdminQuery(
    $count: Int!
    $cursor: String
    $isRecurring: Boolean
    $status: [JobStatus]
    $hasStudentRating: Boolean
    $hasTutorRating: Boolean
    $sessionStatus: [JobSessionStatus]
    $requestType: [JobRequestType]
  ) {
    admin {
      ...Jobs_admin @arguments(count: $count, cursor: $cursor)
    }
  }
`;

export default enhancer(Jobs, {
  form: {
    form: 'jobsForm',
  },
  mapStateToProps: state => ({
    filterValues: selector(
      state,
      'status',
      'isRecurring',
      'requestType',
      'hasTutorRating',
      'hasStudentRating',
      'sessionStatus'
    ),
  }),
  mapDispatchToProps: dispatch => ({
    actions: bindActionCreators({ initialize, reset }, dispatch),
  }),
  relay: {
    query,
    variables: {
      count: PAGE_SIZE,
    },
    create: () =>
      createPaginationContainer(
        Jobs,
        {
          admin: graphql`
            fragment Jobs_admin on Admin
              @argumentDefinitions(
                count: { type: "Int" }
                cursor: { type: "String" }
              ) {
              allJobs(
                first: $count
                after: $cursor
                isRecurring: $isRecurring
                status: $status
                hasStudentRating: $hasStudentRating
                hasTutorRating: $hasTutorRating
                sessionStatus: $sessionStatus
                requestType: $requestType
              ) @connection(key: "Jobs_allJobs") {
                edges {
                  node {
                    id
                    status
                    paymentTutor
                    paymentTutor
                    paymentInternal
                    paymentExternal
                    createdAt
                    sessionCount
                    averageTutorRating
                    averageStudentRating
                    acceptedJobOffer {
                      tutor {
                        fullname
                      }
                      jobRequest {
                        student {
                          fullname
                        }
                        subject {
                          description
                        }
                        grade {
                          name
                          gradeGroup {
                            name
                          }
                        }
                      }
                    }
                    sessions {
                      status
                      rating {
                        studentRating
                        studentComment
                        tutorRating
                        tutorComment
                      }
                      timeSlot {
                        duration {
                          totalMinutes
                        }
                        startDate
                      }
                    }
                  }
                }
              }
            }
          `,
        },
        {
          direction: 'forward',
          getConnectionFromProps(props) {
            return props.admin && props.admin.allJobs;
          },
          getFragmentVariables(prevVars, totalCount) {
            return {
              ...prevVars,
              count: totalCount,
            };
          },
          getVariables(props, { count, cursor } /* , fragmentVariables */) {
            return {
              count,
              cursor,
            };
          },
          query,
        }
      ),
  },
});



// WEBPACK FOOTER //
// ./src/modules/job/Jobs.js